// PATH: src/pages/Multimedia.jsx
// VERSION: 2.3 — Thumbnails Cloudinary + tags negro/blanco + filtro multiselect responsive

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Image,
  Text,
  Badge,
  HStack,
  Tag,
  TagLabel,
  VStack,
  Input,
  Flex,
  Button,
  Alert,
  AlertIcon,
  Spinner,
  useBreakpointValue,
  useColorModeValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Checkbox,
} from '@chakra-ui/react';
import { ArrowLeftIcon, ArrowRightIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { cld } from '../utils/cloudinary';

const PAGE_SIZE = 6;
const API = process.env.REACT_APP_API_BASE_URL || '/api';

export default function Multimedia() {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [selectedFolders, setSelectedFolders] = useState([]); // multiselect

  const [data, setData] = useState({ folders: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const isMdOrBelow = useBreakpointValue({ base: true, md: true, lg: false });
  const searchPlaceholder = useBreakpointValue({
    base: 'Buscar…',
    md: 'Buscar por título, carpeta o tipo…',
  });

  useEffect(() => {
    let isMounted = true;
    async function load() {
      setLoading(true);
      setErr(null);
      try {
        const r = await fetch(`${API}/media`);
        const j = await r.json();
        if (!r.ok || j.ok === false) throw new Error(j.error || 'error');
        if (isMounted) setData({ folders: j.folders || [] });
      } catch (e) {
        if (isMounted) setErr('No se pudo cargar la multimedia.');
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const folders = useMemo(
    () => ['Todos', ...data.folders.map(f => f.name)],
    [data]
  );

  const allItems = useMemo(
    () =>
      data.folders.flatMap(f =>
        (f.items || []).map(it => ({ ...it, folder: f.name }))
      ),
    [data]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return allItems
      .filter(x => {
        // Sin selección o "Todos" → no filtra por carpeta
        if (!selectedFolders.length || selectedFolders.includes('Todos')) return true;
        return selectedFolders.includes(x.folder);
      })
      .filter(
        x =>
          !q ||
          x.title?.toLowerCase().includes(q) ||
          x.folder?.toLowerCase().includes(q) ||
          x.type?.toLowerCase().includes(q)
      );
  }, [allItems, query, selectedFolders]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(start, start + PAGE_SIZE);

  function thumbFor(it) {
    if (it.thumbnail) return it.thumbnail;
    if (it.type === 'video' && it.src && !/^https?:\/\//i.test(it.src)) {
      return cld(it.src, {
        resourceType: 'video',
        w: 600,
        h: 400,
        crop: 'fill',
        gravity: 'auto',
        fetch_format: 'jpg',
      });
    }
    if (it.type !== 'video' && it.src && !/^https?:\/\//i.test(it.src)) {
      return cld(it.src, { w: 600, h: 400, crop: 'fill', gravity: 'auto' });
    }
    return it.src;
  }

  const selectedFoldersLabel = useMemo(() => {
    if (!selectedFolders.length || selectedFolders.includes('Todos')) {
      return 'Todas las carpetas';
    }
    if (selectedFolders.length === 1) return selectedFolders[0];
    return `${selectedFolders.length} carpetas seleccionadas`;
  }, [selectedFolders]);

  const activeTagBg = useColorModeValue('black', 'white');
  const activeTagColor = useColorModeValue('white', 'black');

  return (
    <Box bg="bg.default" flex="1">
      <Container maxW="container.xl" py={{ base: 8, md: 12 }}>
        <Heading textAlign="center" mb={6} color="text.default">
          Multimedia
        </Heading>

        {err && (
          <Alert status="error" mb={4}>
            <AlertIcon />
            {err}
          </Alert>
        )}

        <VStack align="stretch" spacing={4} mb={4}>
          <Input
            borderColor="border.default"
            borderWidth="2px"
            placeholder={searchPlaceholder}
            value={query}
            onChange={e => {
              setPage(1);
              setQuery(e.target.value);
            }}
            bg="bg.card"
            _placeholder={{ color: 'text.muted' }}
            _hover={{ borderColor: 'blue' }}
          />

          {isMdOrBelow ? (
            // Dropdown multiselect en md e inferiores
            <Menu closeOnSelect={false}>
              <MenuButton
                as={Button}
                variant="outline"
                rightIcon={<ChevronDownIcon />}
                alignSelf="flex-start"
                borderColor="border.default"
                bg="bg.card"
                _hover={{
                  bg: 'black',
                  color: 'white',
                  borderColor: 'gray.700',
                }}
              >
                {selectedFoldersLabel}
              </MenuButton>
              <MenuList maxH="260px" overflowY="auto">
                <MenuItem>
                  <Checkbox
                    isChecked={
                      !selectedFolders.length || selectedFolders.includes('Todos')
                    }
                    isIndeterminate={
                      selectedFolders.length > 0 && !selectedFolders.includes('Todos')
                    }
                    onChange={() => {
                      setPage(1);
                      setSelectedFolders([]); // "Todos"
                    }}
                  >
                    Todas
                  </Checkbox>
                </MenuItem>
                {folders
                  .filter(name => name !== 'Todos')
                  .map(name => (
                    <MenuItem key={name}>
                      <Checkbox
                        isChecked={selectedFolders.includes(name)}
                        onChange={() => {
                          setPage(1);
                          setSelectedFolders(prev =>
                            prev.includes(name)
                              ? prev.filter(n => n !== name)
                              : [...prev, name]
                          );
                        }}
                      >
                        {name}
                      </Checkbox>
                    </MenuItem>
                  ))}
              </MenuList>
            </Menu>
          ) : (
            // Chips en pantallas grandes
            <HStack spacing={2} overflowX="auto" pb={1}>
              {folders.map(name => {
                const isActive =
                  name === 'Todos'
                    ? selectedFolders.length === 0
                    : selectedFolders.includes(name);

                return (
                  <Tag
                    key={name}
                    variant="outline"
                    bg={isActive ? activeTagBg : 'bg.card'}
                    color={isActive ? activeTagColor : 'text.default'}
                    borderColor={isActive ? 'gray.700' : 'border.default'}
                    borderWidth="2px"
                    cursor="pointer"
                    boxShadow={
                      isActive ? '0 0 0 1px rgba(15,23,42,0.5)' : 'none'
                    }
                    _hover={
                      isActive
                        ? {
                          borderColor: 'gray.500',
                          boxShadow:
                            '0 0 0 1px rgba(15,23,42,0.7)',
                        }
                        : {
                          bg: activeTagBg,
                          color: activeTagColor,
                          borderColor: 'gray.700',
                          boxShadow:
                            '0 0 0 1px rgba(15,23,42,0.4)',
                        }
                    }
                    onClick={() => {
                      setPage(1);
                      if (name === 'Todos') {
                        setSelectedFolders([]);
                      } else {
                        setSelectedFolders(prev =>
                          prev.includes(name)
                            ? prev.filter(n => n !== name)
                            : [...prev, name]
                        );
                      }
                    }}
                  >
                    <TagLabel>{name}</TagLabel>
                  </Tag>
                );
              })}
            </HStack>
          )}
        </VStack>

        {loading ? (
          <Flex justify="center" py={12}>
            <Spinner size="lg" />
          </Flex>
        ) : (
          <>
            <Box sx={{ columnCount: { base: 1, sm: 2, md: 3 }, columnGap: '32px' }}>
              {pageItems.map((m, idx) => (
                <Box
                  key={`${m.title}-${idx}`}
                  breakInside="avoid"
                  mb={8}
                  bg="bg.card"
                  border="1px solid"
                  borderColor="border.default"
                  borderRadius="xl"
                  overflow="hidden"
                  boxShadow="sm"
                  transition="all .25s ease"
                  _hover={{
                    transform: 'translateY(-4px)',
                    boxShadow: 'md',
                  }}
                >
                  <Image
                    src={thumbFor(m)}
                    alt={m.title}
                    w="100%"
                    h="200px"
                    objectFit="cover"
                    fallbackSrc="https://via.placeholder.com/400x300?text=Multimedia"
                  />
                  <VStack align="start" spacing={2} p={4}>
                    <Text
                      color="text.default"
                      fontWeight="semibold"
                      noOfLines={2}
                    >
                      {m.title}
                    </Text>
                    <HStack>
                      <Badge variant="solid" colorScheme="blue">
                        {m.folder || 'General'}
                      </Badge>
                      <Badge
                        variant="solid"
                        colorScheme={
                          m.type === 'video' ? 'purple' : 'green'
                        }
                      >
                        {m.type}
                      </Badge>
                    </HStack>
                  </VStack>
                </Box>
              ))}
            </Box>

            {filtered.length > PAGE_SIZE && (
              <Flex mt={8} justify="center" align="center" gap={4}>
                <Button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  isDisabled={page === 1}
                  variant="outline"
                  leftIcon={<ArrowLeftIcon color="text.muted" />}
                  _hover={{ bg: 'whiteAlpha.200' }}
                />
                <Text color="text.muted">
                  Página {page} de {totalPages}
                </Text>
                <Button
                  onClick={() =>
                    setPage(p => Math.min(totalPages, p + 1))
                  }
                  isDisabled={page === totalPages}
                  variant="outline"
                  rightIcon={<ArrowRightIcon color="text.muted" />}
                  _hover={{ bg: 'whiteAlpha.200' }}
                />
              </Flex>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}
